/**
 * OpenTelemetry Configuration for RideQuest Frontend
 *
 * Sets up distributed tracing for the React application with:
 * - Automatic fetch instrumentation (captures all GraphQL API calls)
 * - Document load performance instrumentation
 * - User interaction tracking (clicks, form submissions)
 * - Custom span creation via withSpan() utility
 *
 * Export targets:
 * - Development: Console exporter for local debugging in browser DevTools
 * - Production: OTLP HTTP exporter to backend collector
 *
 * Usage:
 *   import { initializeTelemetry, withSpan } from './telemetry';
 *   initializeTelemetry(); // Call once on app mount
 *   await withSpan('operation.name', async (span) => { ... });
 */

import { trace, context, SpanStatusCode, type Span } from '@opentelemetry/api';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { SimpleSpanProcessor, BatchSpanProcessor, ConsoleSpanExporter } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes, defaultResource } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';

// Environment detection
const isDevelopment = import.meta.env.DEV;
const otlpEndpoint = import.meta.env.VITE_OTLP_ENDPOINT || '';

let isInitialized = false;

/**
 * Initialize the OpenTelemetry tracing pipeline.
 * Should be called once during application bootstrap (e.g., in App.tsx useEffect).
 * Safe to call multiple times - subsequent calls are no-ops.
 */
export function initializeTelemetry(): void {
  if (isInitialized) return;
  isInitialized = true;

  // Resource attributes identify this service in traces
  const resource = defaultResource().merge(
    resourceFromAttributes({
      [ATTR_SERVICE_NAME]: 'ridequest-frontend',
      [ATTR_SERVICE_VERSION]: '1.0.0',
      'deployment.environment': isDevelopment ? 'development' : 'production',
    })
  );

  // Build span processors based on environment
  const spanProcessors = [];

  // Development: Use SimpleSpanProcessor with console for immediate output
  if (isDevelopment) {
    spanProcessors.push(new SimpleSpanProcessor(new ConsoleSpanExporter()));
  }

  // Production: Use BatchSpanProcessor with OTLP for efficient export
  if (otlpEndpoint) {
    const otlpExporter = new OTLPTraceExporter({
      url: otlpEndpoint,
      headers: { 'Content-Type': 'application/json' },
    });
    spanProcessors.push(new BatchSpanProcessor(otlpExporter));
  }

  // In SDK v2.x, span processors must be passed via constructor config
  const provider = new WebTracerProvider({
    resource,
    spanProcessors,
  });

  // Register provider globally and use Zone.js for async context propagation
  provider.register({
    contextManager: new ZoneContextManager(),
  });

  // Register automatic instrumentations
  registerInstrumentations({
    instrumentations: [
      // Instrument all fetch() calls - captures GraphQL requests automatically
      new FetchInstrumentation({
        // Propagate trace headers to API origins for distributed tracing
        propagateTraceHeaderCorsUrls: [
          /localhost:5000/,    // Local API server
          /mythos365\.com/,    // Production API
        ],
        clearTimingResources: true,
        // Enrich fetch spans with request/response metadata
        applyCustomAttributesOnSpan: (span: Span, request: Request | RequestInit, result: Response | FetchError | unknown) => {
          if (request instanceof Request) {
            span.setAttribute('http.url', request.url);
            span.setAttribute('http.method', request.method);
          }
          if (result instanceof Response) {
            span.setAttribute('http.status_code', result.status);
          }
        },
      }),
      // Track page load performance (navigation timing, resource timing)
      new DocumentLoadInstrumentation(),
      // Track user interactions for UX observability
      new UserInteractionInstrumentation({
        eventNames: ['click', 'submit'],
      }),
    ],
  });

  if (isDevelopment) {
    console.info('[Telemetry] OpenTelemetry initialized (console exporter)');
  }
}

// Workaround type for FetchInstrumentation error parameter
type FetchError = { status?: number; message?: string };

/**
 * Get the global tracer instance for creating custom spans.
 */
export function getTracer() {
  return trace.getTracer('ridequest-frontend', '1.0.0');
}

/**
 * Execute an async function within a traced span.
 * Automatically handles span lifecycle, error recording, and status setting.
 *
 * @param name - Span name following dot notation (e.g., 'auth.login', 'booking.create')
 * @param fn - Async function to execute. Receives the span for adding custom attributes.
 * @returns The result of the async function
 *
 * @example
 * const result = await withSpan('auth.login', async (span) => {
 *   span.setAttribute('user.email', email);
 *   return await performLogin(email, password);
 * });
 */
export async function withSpan<T>(
  name: string,
  fn: (span: Span) => Promise<T>
): Promise<T> {
  const tracer = getTracer();
  const span = tracer.startSpan(name);

  try {
    // Execute function within the span's context for proper parent-child correlation
    const result = await context.with(trace.setSpan(context.active(), span), () => fn(span));
    span.setStatus({ code: SpanStatusCode.OK });
    return result;
  } catch (error) {
    // Record the error on the span for visibility in trace backends
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: error instanceof Error ? error.message : 'Unknown error',
    });
    if (error instanceof Error) {
      span.recordException(error);
    }
    throw error;
  } finally {
    span.end();
  }
}
